import { ref, uploadBytesResumable } from 'firebase/storage';
import type { UploadTask } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  orderBy,
  limit,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { db, storage, functions, auth } from '../firebase';
import { APP_ANDROID } from '@shared/app-telefono/appAndroid';
import type {
  ActiveVersion,
  AppRelease,
  ParseApkMetadataResponse,
  PublishFormData,
  ReleaseNotesDetailed,
} from '../types/AppMobileTypes';

export type MobileAppId = 'yumacall' | 'yumawork' | 'yumafly' | 'yumastock';

export interface MobileAppConfig {
  id: MobileAppId;
  label: string;
  stagingPath: string;
  finalApkPath: string;
  configDocPath: string;
  releasesCollection: string;
  /**
   * Come si chiama il file APK prodotto dalla compilazione di QUESTA app.
   *
   * ⚠️ Serve al rilevatore della cartella locale, che prima cercava sempre e comunque
   * `yumacall.apk`: puntando la cartella di YumaWORK non trovava niente, e puntando quella
   * di YumaCALL mentre era selezionata un'altra app proponeva di pubblicare l'APK sbagliato
   * sotto il nome giusto.
   */
  apkFileName: string;
  /**
   * Il nome del pacchetto Android che questo APK DEVE avere.
   *
   * ⚠️ È LA GUARDIA CHE CHIUDE IL PROBLEMA ALLA RADICE. I percorsi si possono sempre sbagliare
   * — con la cartella sbagliata, trascinando il file a mano, o domani da un punto d'ingresso
   * che oggi non esiste. Il pacchetto invece sta DENTRO l'APK e lo legge la Cloud Function:
   * confrontarlo con quello atteso rende impossibile pubblicare un'app sopra un'altra, invece
   * di renderlo soltanto improbabile.
   */
  packageName: string;
}

export const MOBILE_APPS: Record<MobileAppId, MobileAppConfig> = {
  yumacall: {
    id: 'yumacall',
    label: 'YumaCALL',
    stagingPath: 'app_releases/_staging.apk',
    finalApkPath: 'app_releases/yumacall.apk',
    configDocPath: APP_ANDROID.yumacall.documentoVersione,
    releasesCollection: 'app_releases',
    apkFileName: 'yumacall.apk',
    packageName: 'com.yumaos.yumacall',
  },
  yumawork: {
    id: 'yumawork',
    label: 'YumaWORK',
    stagingPath: 'app_releases_yumawork/_staging.apk',
    finalApkPath: 'app_releases_yumawork/yumawork.apk',
    configDocPath: APP_ANDROID.yumawork.documentoVersione,
    releasesCollection: 'app_releases_yumawork',
    apkFileName: 'yumawork.apk',
    packageName: 'com.probusiness.yumawork',
  },
  yumafly: {
    id: 'yumafly',
    label: 'YumaFLY',
    stagingPath: 'app_releases_yumafly/_staging.apk',
    finalApkPath: 'app_releases_yumafly/yumafly.apk',
    configDocPath: APP_ANDROID.yumafly.documentoVersione,
    releasesCollection: 'app_releases_yumafly',
    apkFileName: 'yumafly.apk',
    packageName: 'com.yumaos.yumafly',
  },
  yumastock: {
    id: 'yumastock',
    label: 'YumaSTOCK',
    stagingPath: 'app_releases_yumastock/_staging.apk',
    finalApkPath: 'app_releases_yumastock/yumastock.apk',
    configDocPath: APP_ANDROID.yumastock.documentoVersione,
    releasesCollection: 'app_releases_yumastock',
    apkFileName: 'yumastock.apk',
    packageName: 'com.probusiness.yumastock',
  },
};

export function uploadApkToStagingForApp(file: File, appId: MobileAppId): UploadTask {
  const config = MOBILE_APPS[appId];
  const storageRef = ref(storage, config.stagingPath);
  return uploadBytesResumable(storageRef, file, {
    contentType: 'application/vnd.android.package-archive',
    // ⚠️ ANCHE IL FILE DI TRANSITO HA UN NOME DECENTE. Su Storage si chiama `_staging.apk`
    // perché è la casella dove le app di passaggio si appoggiano a turno, ma chi lo scarica per
    // provarlo prima di pubblicare se lo ritrova come «_staging (3).apk» nella cartella dei
    // download, indistinguibile dalle altre app della piattaforma.
    contentDisposition: `attachment; filename="${config.apkFileName}"`,
  });
}

export async function parseApkMetadata(
  storagePath: string,
  finalPath: string,
  /** Il pacchetto che questo APK deve avere: la Cloud Function rifiuta se non combacia. */
  expectedPackageName: string,
): Promise<ParseApkMetadataResponse> {
  const callable = httpsCallable<
    { storagePath: string; finalPath: string; expectedPackageName: string },
    { success: boolean; versionCode: number; versionName: string; packageName: string; downloadUrl: string; sizeBytes: number }
  >(functions, 'parseApkMetadata');

  // finalPath dice alla CF DOVE mettere l'APK (dipende dall'app selezionata). Senza,
  // la CF ricadrebbe sul percorso di yumacall — era la causa del bug che instradava
  // l'APK di YumaWORK sopra quello di YumaCall.
  const result = await callable({ storagePath, finalPath, expectedPackageName });
  return {
    versionCode: result.data.versionCode,
    versionName: result.data.versionName,
    packageName: result.data.packageName,
    downloadUrl: result.data.downloadUrl,
    sizeBytes: result.data.sizeBytes,
  };
}

function flattenReleaseNotes(detailed: ReleaseNotesDetailed): string[] {
  const notes: string[] = [];
  if (detailed.novita.length > 0) {
    notes.push('--- Novità ---');
    detailed.novita.forEach((n) => notes.push(`* ${n}`));
  }
  if (detailed.miglioramenti.length > 0) {
    notes.push('--- Miglioramenti ---');
    detailed.miglioramenti.forEach((n) => notes.push(`* ${n}`));
  }
  if (detailed.correzioni.length > 0) {
    notes.push('--- Correzioni ---');
    detailed.correzioni.forEach((n) => notes.push(`* ${n}`));
  }
  return notes;
}

export async function getActiveVersionForApp(appId: MobileAppId): Promise<ActiveVersion | null> {
  const config = MOBILE_APPS[appId];
  const snap = await getDoc(doc(db, config.configDocPath));
  if (!snap.exists()) return null;
  return snap.data() as ActiveVersion;
}

export async function getReleaseHistoryForApp(appId: MobileAppId, maxResults = 20): Promise<AppRelease[]> {
  const config = MOBILE_APPS[appId];
  const q = query(
    collection(db, config.releasesCollection),
    orderBy('versionCode', 'desc'),
    limit(maxResults)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ ...d.data() } as AppRelease));
}

export async function publishReleaseForApp(data: PublishFormData, appId: MobileAppId): Promise<void> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Utente non autenticato');

  const config = MOBILE_APPS[appId];
  const now = Timestamp.now();
  const releaseNotes = flattenReleaseNotes(data.releaseNotesDetailed);

  const activeVersionData: ActiveVersion = {
    versionCode: data.versionCode,
    versionName: data.versionName,
    apkStoragePath: config.finalApkPath,
    apkDownloadUrl: data.apkDownloadUrl,
    apkSizeBytes: data.apkSizeBytes,
    packageName: data.packageName,
    forceUpdate: data.forceUpdate,
    releaseNotes,
    releaseNotesDetailed: data.releaseNotesDetailed,
    published_by: uid,
    published_at: now,
  };

  const releaseData: AppRelease = {
    ...activeVersionData,
    status: 'published',
    created_by: uid,
    created_at: now,
  };

  const batch = writeBatch(db);

  const currentVersion = await getActiveVersionForApp(appId);
  if (currentVersion && currentVersion.versionCode !== data.versionCode) {
    const prevReleaseRef = doc(db, config.releasesCollection, String(currentVersion.versionCode));
    const prevSnap = await getDoc(prevReleaseRef);
    if (prevSnap.exists()) {
      batch.update(prevReleaseRef, { status: 'superseded' });
    }
  }

  batch.set(doc(db, config.configDocPath), activeVersionData);
  batch.set(doc(db, config.releasesCollection, String(data.versionCode)), releaseData);

  await batch.commit();
}
